import React from 'react';
import { LiquidationAsset } from '../coinLiquidation/types';
import { WalletTokenState, getTokensByNetwork } from '../coinLiquidation/walletTokenFetcher';
import { formatTokenBalance, formatUSDBalance } from '../utils/formatters';
import { canSelectToken } from '../utils/tokenUtils';
import { SelectAllCheckbox } from './SelectAllCheckbox';

interface TokenListProps {
  walletTokenState: WalletTokenState;
  customLiquidation: Record<string, unknown>;
  nativeLockUsd: number;
  isDataFromCache: boolean;
  selectAll: boolean;
  onSelectCoin: (network: any, token?: LiquidationAsset) => void;
  onRefresh: () => void;
  onSelectAll: () => void;
}

export const TokenList: React.FC<TokenListProps> = ({
  walletTokenState,
  customLiquidation,
  nativeLockUsd,
  isDataFromCache,
  selectAll,
  onSelectCoin,
  onRefresh,
  onSelectAll,
}) => {
  const totalChains =
    walletTokenState.totalChains || walletTokenState.chainStatuses?.length || 0;
  const chainOrder =
    walletTokenState.chainStatuses && walletTokenState.chainStatuses.length > 0
      ? walletTokenState.chainStatuses.map((chain) => chain.chainId)
      : Object.keys(walletTokenState.tokenByNetwork).map((id) => Number(id));

  if (walletTokenState.isLoading) {
    return (
      <div className="bg-gray-800 rounded-lg p-4 sm:p-5 md:p-6">
        <div className="py-6 sm:py-8">
          <div className="bg-gray-700 rounded-lg p-4 sm:p-5 md:p-6 mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-white">Fetching Tokens...</h3>
              <span className="text-xs sm:text-sm text-gray-400">
                {walletTokenState.completedChains || 0} / {totalChains} chains
              </span>
            </div>

            {/* Overall Progress Bar */}
            <div className="w-full bg-gray-600 rounded-full h-2.5 sm:h-3 mb-3 sm:mb-4">
              <div
                className="bg-blue-600 h-2.5 sm:h-3 rounded-full transition-all duration-500"
                style={{
                  width: `${((walletTokenState.completedChains || 0) / (totalChains || 1)) * 100}%`
                }}
              ></div>
            </div>

            {/* Chain-by-Chain Status */}
            <div className="space-y-2">
              {(walletTokenState.chainStatuses || []).map((chainStatus) => (
                <div key={chainStatus.chainId} className="flex items-center justify-between text-xs sm:text-sm">
                  <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                    <div className={`w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full flex items-center justify-center flex-shrink-0 ${chainStatus.status === 'completed' ? 'bg-green-500' :
                        chainStatus.status === 'loading' ? 'bg-blue-500 animate-pulse' :
                          chainStatus.status === 'error' ? 'bg-red-500' :
                            'bg-gray-500'
                      }`}>
                      {chainStatus.status === 'completed' && (
                        <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      {chainStatus.status === 'error' && (
                        <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span className="text-white font-medium truncate">{chainStatus.chainName}</span>
                  </div>
                  <div className="text-gray-400 text-xs sm:text-sm ml-2 flex-shrink-0">
                    {chainStatus.status === 'completed' && (
                      <span className="text-green-400">{chainStatus.tokenCount} tokens</span>
                    )}
                    {chainStatus.status === 'loading' && (
                      <span className="text-blue-400 animate-pulse">Loading...</span>
                    )}
                    {chainStatus.status === 'error' && (
                      <span className="text-red-400">Failed</span>
                    )}
                    {chainStatus.status === 'pending' && (
                      <span>Pending</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-400 mx-auto mb-2"></div>
            <p className="text-gray-400 text-xs sm:text-sm">Scanning blockchain for tokens...</p>
          </div>
        </div>
      </div>
    );
  }

  if (walletTokenState.error) {
    return (
      <div className="bg-gray-800 rounded-lg p-4 sm:p-5 md:p-6">
        <div className="text-center py-6 sm:py-8 px-2">
          <p className="text-red-400 mb-4 text-sm  break-words">Error: {walletTokenState.error}</p>
          <button
            onClick={onRefresh}
            className="px-4 sm:px-6 py-2.5 sm:py-2 bg-red-600 hover:bg-red-700 active:bg-red-800 rounded text-xs sm:text-sm font-medium min-h-[48px] sm:min-h-[44px] transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-4 sm:p-5 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <h2 className="text-lg sm:text-xl font-semibold">Select Tokens to Sell</h2>
          {isDataFromCache && (
            <span className="px-2 py-1 bg-yellow-600 text-yellow-100 text-xs rounded-full whitespace-nowrap">
              Cached
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={onRefresh}
            disabled={walletTokenState.isLoading}
            className="px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:bg-gray-600 rounded text-xs sm:text-sm font-medium min-h-[44px] sm:min-h-[40px] transition-colors"
          >
            <span className="hidden sm:inline">Force Refresh</span>
            <span className="sm:hidden">Refresh</span>
          </button>
        </div>
      </div>

      <SelectAllCheckbox
        selectAll={selectAll}
        nativeLockUsd={nativeLockUsd}
        onChange={onSelectAll}
      />

      <div className="space-y-3 sm:space-y-4">
        {chainOrder.map(chainId => {
          const networkTokens = getTokensByNetwork(walletTokenState, chainId)
            .filter(token => {
              const usdBalance = Number(token.USDBalance) || 0;
              return usdBalance > 0;
            })
            .sort((a, b) => {
              // Native tokens first
              if (a.isNative && !b.isNative) return -1;
              if (!a.isNative && b.isNative) return 1;

              // Then sort by USD price descending
              const aPrice = Number(a.USDBalance) || 0;
              const bPrice = Number(b.USDBalance) || 0;
              return bPrice - aPrice;
            });

          const chainStatus = walletTokenState.chainStatuses?.find(cs => cs.chainId === chainId);
          const networkName =
            networkTokens[0]?.network?.name || chainStatus?.chainName || chainId.toString();
          const network =
            networkTokens[0]?.network || ({ chainId, name: networkName } as any);
          const isNetworkSelected = (customLiquidation[chainId.toString()] as any)?.isSelected || false;

          return (
            <div key={chainId} className="border border-gray-600 rounded-lg p-3 sm:p-4">
              {/* Network Header */}
              <div
                className="flex items-center justify-between cursor-pointer hover:bg-gray-700 active:bg-gray-600 rounded p-2 -m-2 min-h-[48px] sm:min-h-[44px]"
                onClick={() => onSelectCoin(network)}
              >
                <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                  <input
                    type="checkbox"
                    checked={isNetworkSelected}
                    onChange={() => onSelectCoin(network)}
                    disabled={!networkTokens.length}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: '24px', height: '24px', minWidth: '24px', minHeight: '24px' }}
                    className={`text-green-600 bg-gray-700 border-gray-600 rounded focus:ring-green-500 flex-shrink-0 ${!networkTokens.length ? 'opacity-30' : ''
                      }`}
                  />
                  <div className="flex items-center space-x-2 min-w-0 flex-1">
                    <h3 className="text-base sm:text-lg font-semibold text-white truncate">{networkName}</h3>
                    {chainStatus && (
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${chainStatus.status === 'completed' ? 'bg-green-500' :
                          chainStatus.status === 'loading' ? 'bg-blue-500 animate-pulse' :
                            chainStatus.status === 'error' ? 'bg-red-500' :
                              'bg-gray-500'
                        }`} title={`${chainStatus.status}`}></div>
                    )}
                  </div>
                </div>
                <div className="text-xs sm:text-sm text-gray-400 whitespace-nowrap ml-2">
                  {networkTokens.length > 0 ? `${networkTokens.length} available` : 'no assets'}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-600 my-3"></div>

              {/* Token List */}
              {isNetworkSelected && networkTokens.length > 0 && (
                <div className="space-y-2 sm:space-y-2">
                  {networkTokens.map((token, index) => {
                    const isTokenSelected = (customLiquidation[chainId.toString()] as any)?.[token.symbol]?.isSelected || false;
                    const canSelect = canSelectToken(token, nativeLockUsd);
                    const isNativeToken = token.isNative;
                    const symbol = isNativeToken ? token.nativeCurrency.symbol : token.symbol;

                    return (
                      <div
                        key={index}
                        className={`bg-gray-700 rounded-lg p-2.5 sm:p-3 flex items-center justify-between transition-colors min-h-[48px] sm:min-h-[56px] ${canSelect ? 'hover:bg-gray-600 active:bg-gray-500' : 'opacity-60'
                          }`}
                      >
                        <div onClick={() => { onSelectCoin(token.network, token) }} className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                          <input
                            type="checkbox"
                            checked={isTokenSelected}
                            // onChange={() => onSelectCoin(token.network, token)}
                            disabled={!canSelect}
                            style={{ width: '24px', height: '24px', minWidth: '24px', minHeight: '24px' }}
                            className={`text-green-600 bg-gray-600 border-gray-500 rounded focus:ring-green-500 flex-shrink-0 ${!canSelect ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center space-x-2 flex-wrap">
                              <h4 className="font-medium text-white text-sm ">{symbol}</h4>
                              {isNativeToken && (
                                <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-600 text-blue-100 text-xs rounded-full whitespace-nowrap">
                                  Native
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">
                              Balance: {formatTokenBalance(token.balanceString || token.stringBalance || token.balance || 0)}
                            </p>
                            <p className="text-xs text-gray-500 font-mono truncate max-w-full">
                              {token.address}
                            </p>
                            {isNativeToken && (
                              <p className="text-xs text-blue-400 mt-1">
                                {canSelect ? `Will lock $${nativeLockUsd} for gas` : `Insufficient balance (need $${nativeLockUsd} minimum)`}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 sm:space-x-3 ml-2 flex-shrink-0">
                          <div className="text-right">
                            <p className="text-xs sm:text-sm font-medium text-green-400 whitespace-nowrap">
                              {formatUSDBalance(token.USDBalance)}
                            </p>
                            {token.error && (
                              <p className="text-xs text-red-400 truncate max-w-[100px] sm:max-w-none">{token.error}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
